import { useMemo } from "react";
import { buildHallOfFame, buildLedger, buildManagerProfiles, buildMilestones } from "@averagejoesff/shared";
import type { LeagueHistory } from "@averagejoesff/shared";
import { AppLink } from "../../components/AppLink";
import { ResourceStatus } from "../../components/ResourceStatus";
import { StateMessage } from "../../components/StateMessage";
import { useLeagueHistory } from "../../lib/leagueHistory";
import { playoffOddsFor } from "../../lib/playoffOdds";
import { useMyManager } from "../../lib/viewer";
import { useRoute } from "../../lib/router";
import { HallOfFameView } from "./HallOfFameView";
import { LuckView } from "./LuckView";
import { ManagerGrid } from "./ManagerGrid";
import { MilestonesView } from "./MilestonesView";
import { ProfileView } from "./ProfileView";
import { HALL_OF_FAME_PATH, LUCK_PATH, MANAGERS_PATH, MILESTONES_PATH } from "./paths";
import { indexPeople } from "./people";

const HALL_OF_FAME_SEGMENT = HALL_OF_FAME_PATH.slice(MANAGERS_PATH.length + 1);
const LUCK_SEGMENT = LUCK_PATH.slice(MANAGERS_PATH.length + 1);
const MILESTONES_SEGMENT = MILESTONES_PATH.slice(MANAGERS_PATH.length + 1);

/** Each view's own heading; the League tabs above the page already say where you are. */
const HEADINGS: Record<string, { title: string; description: string }> = {
  [HALL_OF_FAME_SEGMENT]: { title: "Hall of Fame", description: "The league's all-time records." },
  [MILESTONES_SEGMENT]: { title: "Milestones", description: "Round numbers and records within reach." },
  [LUCK_SEGMENT]: { title: "Luck", description: "Who the schedule has been kind to." },
};
const MANAGERS_HEADING = { title: "Managers", description: "Every manager's career." };

/**
 * Every manager's career and the league's all-time records. One site-wide
 * page with five views picked by the URL: `/managers` (everyone),
 * `/managers/hall-of-fame`, `/managers/milestones`, `/managers/luck`, and
 * `/managers/<id>` for one profile, so each is a link that can be shared. The
 * tabs that switch between the fixed views belong to the nav's League group (`SectionTabs`).
 */
export function ManagersView() {
  const history = useLeagueHistory();
  const pathname = useRoute();
  const segment = pathname.slice(MANAGERS_PATH.length).replace(/^\/+|\/+$/g, "");
  const heading = HEADINGS[segment] ?? MANAGERS_HEADING;

  return (
    <section className="feature" aria-labelledby="managers-heading">
      <div className="feature__intro">
        <h2 id="managers-heading" className="feature__title">
          {heading.title}
        </h2>
        <p className="feature__description">{heading.description}</p>
      </div>

      <ResourceStatus resource={history} loading="Loading managers…" error="Could not load managers" />
      {history.status === "ready" &&
        (history.data === null ? (
          <StateMessage variant="empty" title="No league history yet" detail="Check back once the first results are in." />
        ) : (
          <ManagersBody history={history.data} segment={segment} />
        ))}
    </section>
  );
}

function ManagersBody({ history, segment }: { history: LeagueHistory; segment: string }) {
  const [myId, setMyId] = useMyManager();
  const profiles = useMemo(() => buildManagerProfiles(history), [history]);
  const people = useMemo(() => indexPeople(history), [history]);
  const hallOfFame = useMemo(() => (segment === HALL_OF_FAME_SEGMENT ? buildHallOfFame(history) : null), [history, segment]);
  const milestones = useMemo(
    () => (segment === MILESTONES_SEGMENT ? buildMilestones(history, playoffOddsFor(history)) : null),
    [history, segment],
  );

  // Only the luck view replays every game.
  const ledger = useMemo(() => (segment === LUCK_SEGMENT ? buildLedger(history) : []), [history, segment]);

  function renderContent() {
    if (segment === "") return <ManagerGrid profiles={profiles} myId={myId} />;
    if (hallOfFame) return <HallOfFameView hallOfFame={hallOfFame} people={people} />;
    if (milestones) return <MilestonesView milestones={milestones} people={people} myId={myId} />;
    if (segment === LUCK_SEGMENT) return <LuckView history={history} ledger={ledger} people={people} myId={myId} />;
    const profile = profiles.find((p) => p.personId === segment);
    if (profile) {
      return <ProfileView profile={profile} people={people} isMe={profile.personId === myId} onSetMe={() => setMyId(profile.personId)} />;
    }
    return (
      <>
        <StateMessage variant="empty" title="No such manager" detail="That name isn't in the league history." />
        <AppLink href={MANAGERS_PATH} className="feature__edit-toggle">
          All managers
        </AppLink>
      </>
    );
  }

  return renderContent();
}
